import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, BarChart3, Eye } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    // No orders in demo mode
    setOrders([]);
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    toast.info('Demo mode: Order status not saved');
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'confirmed':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'shipped':
        return { bg: '#e9d5ff', text: '#6b21a8' };
      case 'delivered':
        return { bg: '#d1fae5', text: '#065f46' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }} data-testid="admin-orders-page">
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div style={{ padding: '0 2rem', marginBottom: '2rem' }}>
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '50px', marginBottom: '0.5rem' }} />
          <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#A44A66' }}>ADMIN PANEL</h2>
        </div>
        <Link to="/admin" className="admin-sidebar-link" data-testid="admin-nav-dashboard">
          <BarChart3 size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Dashboard
        </Link>
        <Link to="/admin/products" className="admin-sidebar-link" data-testid="admin-nav-products">
          <Package size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Products
        </Link>
        <Link to="/admin/orders" className="admin-sidebar-link active" data-testid="admin-nav-orders">
          <ShoppingBag size={20} style={{ marginRight: '0.75rem', display: 'inline' }} />
          Orders
        </Link>
        <div style={{ padding: '2rem', borderTop: '1px solid #e5e5e5', marginTop: '2rem' }}>
          <Link to="/" style={{ color: '#666', textDecoration: 'none', fontSize: '0.9rem' }} data-testid="back-to-site">← Back to Site</Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '2rem' }} data-testid="orders-title">Orders</h1>

        {/* Orders Table */}
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} data-testid="orders-table">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f8f8', borderBottom: '2px solid #e5e5e5' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Order ID</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Customer</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Phone</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Total</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
                      <Package size={48} style={{ color: '#ddd', margin: '0 auto 1rem' }} />
                      <p style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>No Orders Yet</p>
                      <p style={{ fontSize: '0.9rem' }}>Demo mode: Orders placed through checkout are not saved</p>
                    </td>
                  </tr>
                ) : orders.map((order) => {
                  const statusStyle = getStatusColor(order.status);
                  return (
                    <tr key={order.id} style={{ borderBottom: '1px solid #e5e5e5' }} data-testid={`order-row-${order.id}`}>
                      <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.85rem' }} data-testid="table-order-id">
                        {order.id.substring(0, 8)}...
                      </td>
                      <td style={{ padding: '1rem', fontWeight: '600' }} data-testid="table-customer-name">{order.customer_name}</td>
                      <td style={{ padding: '1rem', fontSize: '0.9rem' }} data-testid="table-customer-email">{order.customer_email}</td>
                      <td style={{ padding: '1rem' }} data-testid="table-customer-phone">{order.customer_phone}</td>
                      <td style={{ padding: '1rem', fontWeight: '700', color: '#A44A66' }} data-testid="table-order-total">₹{order.total_amount.toLocaleString()}</td>
                      <td style={{ padding: '1rem' }}>
                        <Select value={order.status} onValueChange={(value) => handleStatusChange(order.id, value)}>
                          <SelectTrigger
                            style={{
                              width: '140px',
                              background: statusStyle.bg,
                              color: statusStyle.text,
                              border: 'none',
                              fontWeight: '600'
                            }}
                            data-testid="order-status-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.9rem' }} data-testid="table-order-date">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <Dialog open={showDialog && selectedOrder?.id === order.id} onOpenChange={setShowDialog}>
                          <DialogTrigger asChild>
                            <button
                              onClick={() => viewOrderDetails(order)}
                              style={{ padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                              data-testid="view-order-btn"
                            >
                              <Eye size={16} />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-details-dialog">
                            <DialogHeader>
                              <DialogTitle data-testid="dialog-title">Order Details</DialogTitle>
                            </DialogHeader>
                            {selectedOrder && (
                              <div>
                                <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f8f8', borderRadius: '8px' }}>
                                  <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem' }}>Customer Information</h3>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                      <p style={{ fontSize: '0.85rem', color: '#666' }}>Name</p>
                                      <p style={{ fontWeight: '600' }} data-testid="detail-customer-name">{selectedOrder.customer_name}</p>
                                    </div>
                                    <div>
                                      <p style={{ fontSize: '0.85rem', color: '#666' }}>Email</p>
                                      <p style={{ fontWeight: '600' }} data-testid="detail-customer-email">{selectedOrder.customer_email}</p>
                                    </div>
                                    <div>
                                      <p style={{ fontSize: '0.85rem', color: '#666' }}>Phone</p>
                                      <p style={{ fontWeight: '600' }} data-testid="detail-customer-phone">{selectedOrder.customer_phone}</p>
                                    </div>
                                    <div>
                                      <p style={{ fontSize: '0.85rem', color: '#666' }}>Payment Method</p>
                                      <p style={{ fontWeight: '600' }} data-testid="detail-payment-method">{selectedOrder.payment_method}</p>
                                    </div>
                                  </div>
                                  <div style={{ marginTop: '1rem' }}>
                                    <p style={{ fontSize: '0.85rem', color: '#666' }}>Address</p>
                                    <p style={{ fontWeight: '600' }} data-testid="detail-customer-address">{selectedOrder.customer_address}</p>
                                  </div>
                                </div>
                                <div>
                                  <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem' }}>Order Items</h3>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {selectedOrder.items.map((item, index) => (
                                      <div
                                        key={index}
                                        style={{ padding: '1rem', border: '1px solid #e5e5e5', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        data-testid={`order-item-${index}`}
                                      >
                                        <div>
                                          <p style={{ fontWeight: '600', marginBottom: '0.25rem' }} data-testid="item-name">{item.product_name}</p>
                                          <p style={{ fontSize: '0.9rem', color: '#666' }} data-testid="item-quantity">Quantity: {item.quantity}</p>
                                        </div>
                                        <div style={{ fontWeight: '700', color: '#A44A66', fontSize: '1.1rem' }} data-testid="item-price">
                                          ₹{(item.price * item.quantity).toLocaleString()}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8f8f8', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '1.3rem', fontWeight: '700' }}>Total:</span>
                                    <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#A44A66' }} data-testid="detail-total">₹{selectedOrder.total_amount.toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;